'use client';

import * as React from 'react';

import type { UseEmblaCarouselType } from 'embla-carousel-react';
import useEmblaCarousel from 'embla-carousel-react';

import { cn } from '@wwe-portal/ui/lib/lib';

import { Each } from '../utils';
import { ClickableDiv } from '../button';

type CarouselOptions = Parameters<typeof useEmblaCarousel>[0];
type CarouselPlugins = Parameters<typeof useEmblaCarousel>[1];
type CarouselApi = UseEmblaCarouselType[1];

interface CarouselProps {
  opts?: CarouselOptions;
  plugins?: CarouselPlugins;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  isFlooded?: boolean;
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

export function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

type ExtendedCarouselProps = CarouselProps &
  React.HTMLAttributes<HTMLDivElement>;
const Carousel = React.forwardRef<HTMLDivElement, ExtendedCarouselProps>(
  (
    {
      orientation = 'horizontal',
      opts = {},
      setApi,
      plugins,
      className,
      isFlooded,
      children,
      ...props
    }: ExtendedCarouselProps,
    ref
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((currentApi: CarouselApi) => {
      if (!currentApi) {
        return;
      }

      setCanScrollPrev(currentApi.canScrollPrev());
      setCanScrollNext(currentApi.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api.off('select', onSelect);
      };
    }, [api, onSelect]);

    const value = React.useMemo(() => {
      return {
        carouselRef,
        api,
        opts,
        orientation,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      };
    }, [
      carouselRef,
      api,
      opts,
      orientation,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
    ]);

    return (
      <CarouselContext.Provider value={value}>
        <section
          aria-roledescription="carousel"
          className={cn('relative', className, {
            '-mx-5 overflow-hidden px-5 md:-mx-[30px] md:px-[30px]': isFlooded,
          })}
          onKeyDownCapture={handleKeyDown}
          ref={ref}
          {...props}
        >
          {children}
        </section>
      </CarouselContext.Provider>
    );
  }
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    containerClassName?: string;
  }
>(({ className, containerClassName, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      className={cn('overflow-visible', containerClassName)}
      ref={carouselRef}
    >
      <div
        className={cn(
          'flex',
          orientation === 'horizontal' ? 'ml-4' : 'mt-4 flex-col',
          className
        )}
        ref={ref}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();

  return (
    <div
      aria-roledescription="slide"
      className={cn(
        'min-w-0 shrink-0 grow-0 select-none',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
CarouselItem.displayName = 'CarouselItem';

export const useSelectedSnap = () => {
  const { api } = useCarousel();
  const [currentIndex, setCurrentIndex] = React.useState(
    api?.selectedScrollSnap()
  );

  React.useEffect(() => {
    api?.on('select', (currentApi) => {
      setCurrentIndex(currentApi.selectedScrollSnap());
    });
  }, [api]);
  return currentIndex ?? 0;
};

const CarouselIndicator: React.FC<{
  className?: string;
  indicatorSize?: number;
}> = ({ className }) => {
  const { api } = useCarousel();
  const nodes = api?.slideNodes();
  const currentIndex = useSelectedSnap();

  return (
    <div className={cn('flex flex-row justify-center gap-2', className)}>
      {nodes != null && (
        <Each
          data={nodes}
          render={(_, index) => {
            return (
              <ClickableDiv
                key={index}
                onClick={() => {
                  api?.scrollTo(index);
                }}
                className="p-1"
              >
                <svg
                  className={cn('text-bgColor30', {
                    'opacity-20': index !== currentIndex,
                  })}
                  fill="currentColor"
                  height="10"
                  viewBox="0 0 11 11"
                  width="10"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    fill="currentColor"
                    height="10"
                    rx="5"
                    width="10"
                    x="0.363281"
                    y="0.941406"
                  />
                </svg>
              </ClickableDiv>
            );
          }}
        />
      )}
    </div>
  );
};

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselIndicator,
};
